import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, PackageCheck, Loader2 } from "lucide-react";
import { useCart } from "@/lib/cart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          const o = await axios.get(`${API}/payments/order/${sessionId}`);
          setOrder(o.data);
          setStatus("paid");
          localStorage.removeItem("atrocity-ref");
          clear();
          return;
        }
      } catch {
        // keep polling — Stripe may still be processing
      }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-void newsprint-grain">
      <header className="border-b border-blooddeep/40 bg-void/85 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="payment-brand" className="flex items-center gap-2 group">
            <img src="/covers/atrocity-logo.webp" alt="Atrocity Comix logo" className="h-6 w-6 rounded-full transition-transform duration-200 group-hover:rotate-12" />
            <span className="font-display text-sm sm:text-base text-paper tracking-wide">
              ATROCITY <span className="text-crimson">COMIX</span>
            </span>
          </a>
          <a
            href="/shop"
            data-testid="payment-back-link"
            className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 transition-colors duration-150 hover:text-crimson"
          >
            <ArrowLeft size={14} /> Back to the Shop
          </a>
        </div>
      </header>

      <div className="mx-auto max-w-lg px-4 sm:px-6 py-20 text-center">
        {status === "checking" && (
          <>
            <Loader2 size={44} className="mx-auto text-crimson mb-6 animate-spin" />
            <p data-testid="payment-status-text" className="font-type text-sm text-paper/60">
              Confirming your payment with Stripe...
            </p>
          </>
        )}

        {status === "pending" && (
          <>
            <PackageCheck size={44} className="mx-auto text-paper/60 mb-6" />
            <h1 className="font-display text-2xl uppercase text-paper mb-4">Still Sealing</h1>
            <p data-testid="payment-status-text" className="font-type text-sm text-paper/60">
              Your payment is processing. Your receipt and any download links will arrive by email in a few minutes.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <PackageCheck size={44} className="mx-auto text-paper/60 mb-6" />
            <h1 className="font-display text-2xl uppercase text-paper mb-4">Missing Pact</h1>
            <p data-testid="payment-status-text" className="font-type text-sm text-paper/60">
              This page needs a payment session. If you just paid, check your email for the receipt.
            </p>
          </>
        )}

        {status === "paid" && order && (
          <>
            <PackageCheck size={56} className="mx-auto text-bone mb-6" />
            <h1 className="font-impact uppercase text-3xl text-paper mb-3 tracking-widest">The Pact Is Sealed</h1>
            <p className="font-type text-sm text-paper/60 mb-1">Paid in full. Order ID:</p>
            <p data-testid="payment-order-id" className="font-courier text-xs text-bloodglow break-all mb-8">{order.id}</p>

            <div data-testid="payment-order-summary" className="border-2 border-blooddeep bg-pulp p-5 text-left mb-6">
              <ul className="space-y-1 mb-3">
                {order.items.map((i, idx) => (
                  <li key={idx} className="font-type text-sm text-paper/80">
                    {i.title} <span className="text-paper/40">&times; {i.qty}</span>
                  </li>
                ))}
              </ul>
              <p className="font-marker text-bone text-xl">${order.total.toFixed(2)}</p>
            </div>

            {(order.downloads || []).length > 0 && (
              <div data-testid="payment-downloads" className="border border-cyanx/30 bg-void/60 p-4 text-left mb-6">
                <p className="font-courier text-[10px] uppercase tracking-widest text-cyanx mb-3">
                  Your digital loot — links live 7 days
                </p>
                <ul className="space-y-2">
                  {order.downloads.map((d) => (
                    <li key={d.product_id}>
                      <a
                        data-testid={`download-link-${d.product_id}`}
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-type text-sm text-paper hover:text-crimson transition-colors duration-150 underline decoration-blooddeep underline-offset-4"
                      >
                        Download {d.title} ↓
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="font-marker text-bloodglow text-sm rotate-[-1deg] mb-8">
              {order.contains_physical
                ? "Your physical loot ships in a coffin-grade mailer. Watch your inbox."
                : "A receipt and your links were also ravened to your inbox."}
            </p>
            <a
              data-testid="payment-continue-shopping"
              href="/shop"
              className="inline-block font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-8 py-3.5 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson"
            >
              Back to the Rack
            </a>
          </>
        )}
      </div>
    </div>
  );
}
